import React, { useState, useEffect } from 'react';
import './Loans.css';

export default function Loans() {
  const [loanAmount, setLoanAmount] = useState(5000000); // 50 Lakhs
  const [interestRate, setInterestRate] = useState(8.5); // 8.5%
  const [tenure, setTenure] = useState(20); // 20 years
  
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);

  useEffect(() => {
    const P = Number(loanAmount) || 0;
    const r = (Number(interestRate) || 0) / 12 / 100;
    const n = (Number(tenure) || 0) * 12;
    
    if (n === 0) {
      setEmi(0);
      setTotalPayment(0);
      setTotalInterest(0);
      return;
    }

    if (r === 0) {
      setEmi(P / n);
      setTotalPayment(P);
      setTotalInterest(0);
      return;
    }

    const calculatedEmi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const calculatedTotalPayment = calculatedEmi * n;
    const calculatedTotalInterest = calculatedTotalPayment - P;

    setEmi(Math.round(calculatedEmi));
    setTotalPayment(Math.round(calculatedTotalPayment));
    setTotalInterest(Math.round(calculatedTotalInterest));
  }, [loanAmount, interestRate, tenure]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val);
  };

  // Calculate percentages for splitting graph
  const principalPercent = totalPayment > 0 ? Math.round((loanAmount / totalPayment) * 100) : 100;
  const interestPercent = 100 - principalPercent;

  return (
    <div className="loans-page animate-fade-in-up">
      {/* Page Header */}
      <section className="page-header">
        <div className="container text-center">
          <span className="section-tag" style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>
            Financial Planning
          </span>
          <h1 className="page-title">Home Loans & EMI Calculator</h1>
          <p className="page-subtitle">
            Calculate your monthly investments, check criteria, and make informed choices for your dream properties.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="calculator-section">
        <div className="container">
          <div className="grid-2 calculator-layout">
            {/* Inputs Panel */}
            <div className="premium-card calc-card-input">
              <h3 className="calc-title">EMI Calculator</h3>
              
              {/* Loan Amount */}
              <div className="range-group">
                <div className="range-header">
                  <label>Loan Amount (₹)</label>
                  <input 
                    type="number" 
                    value={loanAmount} 
                    onChange={(e) => setLoanAmount(e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => setLoanAmount(Math.min(100000000, Math.max(100000, Number(loanAmount) || 100000)))}
                  />
                </div>
                <input 
                  type="range" 
                  min="100000" 
                  max="50000000" 
                  step="50000"
                  value={Number(loanAmount) || 100000} 
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="slider-input"
                />
                <div className="range-labels">
                  <span>₹1 Lakh</span>
                  <span>₹500 Lakhs</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="range-group">
                <div className="range-header">
                  <label>Interest Rate (% P.A.)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    value={interestRate} 
                    onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => setInterestRate(Math.min(20, Math.max(1, Number(interestRate) || 8.5)))}
                  />
                </div>
                <input 
                  type="range" 
                  min="5" 
                  max="15" 
                  step="0.05"
                  value={Number(interestRate) || 5} 
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="slider-input"
                />
                <div className="range-labels">
                  <span>5%</span>
                  <span>15%</span>
                </div>
              </div>

              {/* Tenure */}
              <div className="range-group">
                <div className="range-header">
                  <label>Loan Tenure (Years)</label>
                  <input 
                    type="number" 
                    value={tenure} 
                    onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => setTenure(Math.min(30, Math.max(1, Number(tenure) || 20)))}
                  />
                </div>
                <input 
                  type="range" 
                  min="1" 
                  max="30" 
                  step="1"
                  value={Number(tenure) || 1} 
                  onChange={(e) => setTenure(Number(e.target.value))}
                  className="slider-input"
                />
                <div className="range-labels">
                  <span>1 Yr</span>
                  <span>30 Yrs</span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="premium-card calc-card-results flex-column">
              <h3 className="calc-title">Loan Summary</h3>
              
              <div className="summary-emi-badge text-center">
                <span className="emi-lbl">Monthly EMI</span>
                <span className="emi-val">{formatCurrency(emi)}</span>
              </div>

              <div className="summary-stats-list">
                <div className="summary-stat-row">
                  <span className="summary-stat-label">Principal Amount</span>
                  <span className="summary-stat-val">{formatCurrency(loanAmount)}</span>
                </div>
                <div className="summary-stat-row">
                  <span className="summary-stat-label">Total Interest Payable</span>
                  <span className="summary-stat-val text-orange">{formatCurrency(totalInterest)}</span>
                </div>
                <div className="summary-stat-row total-row">
                  <span className="summary-stat-label">Total Amount Payable</span>
                  <span className="summary-stat-val">{formatCurrency(totalPayment)}</span>
                </div>
              </div>

              {/* Graphical Visualizer */}
              <div className="chart-visualizer">
                <div className="chart-bar-container">
                  <div 
                    className="chart-segment principal-segment" 
                    style={{ width: `${principalPercent}%` }}
                    title={`Principal: ${principalPercent}%`}
                  ></div>
                  <div 
                    className="chart-segment interest-segment" 
                    style={{ width: `${interestPercent}%` }}
                    title={`Interest: ${interestPercent}%`}
                  ></div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-dot dot-teal"></span>
                    <span>Principal ({principalPercent}%)</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot dot-orange"></span>
                    <span>Interest ({interestPercent}%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="eligibility-section">
        <div className="container">
          <div className="text-center">
            <span className="section-tag" style={{ backgroundColor: 'var(--color-secondary-light)', color: 'var(--color-secondary)' }}>
              Bank Guidelines
            </span>
            <h2 className="section-title">Loan Eligibility <span>Criteria</span></h2>
            <p className="section-desc">
              Understand the standard parameters banks assess when reviewing your home or plot loan application.
            </p>
          </div>

          <div className="grid-3" style={{ marginTop: '20px' }}>
            {/* Criteria 1 */}
            <div className="premium-card criteria-card">
              <div className="criteria-icon">👥</div>
              <h3>Demographics & Age</h3>
              <ul className="criteria-list-items" style={{ listStyle: 'none', padding: 0, marginTop: '10px', width: '100%' }}>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Minimum Age:</strong> 21 years
                </li>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Maximum Age:</strong> 65 years at loan maturity
                </li>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Citizenship:</strong> Resident Indian or NRI
                </li>
              </ul>
            </div>

            {/* Criteria 2 */}
            <div className="premium-card criteria-card">
              <div className="criteria-icon">💼</div>
              <h3>Profession & Income</h3>
              <ul className="criteria-list-items" style={{ listStyle: 'none', padding: 0, marginTop: '10px', width: '100%' }}>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Salaried:</strong> Min. 2 years experience with current firm for 1 year
                </li>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Self Employed:</strong> Min. 3 years business run with audited returns
                </li>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Min. Income:</strong> ₹35,000 monthly
                </li>
              </ul>
            </div>

            {/* Criteria 3 */}
            <div className="premium-card criteria-card">
              <div className="criteria-icon">📊</div>
              <h3>Financial Health</h3>
              <ul className="criteria-list-items" style={{ listStyle: 'none', padding: 0, marginTop: '10px', width: '100%' }}>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Credit Score:</strong> 700+ preferred for lower rates
                </li>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Existing Debts:</strong> EMI-to-Income ratio should not exceed 50%
                </li>
                <li style={{ marginBottom: '8px', fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                  <strong>Property:</strong> Title must be fully clear and RERA approved
                </li>
              </ul>
            </div>
          </div>

          {/* Documents Box */}
          <div className="premium-card docs-required-box" style={{ marginTop: '40px' }}>
            <h3 style={{ fontSize: '1.45rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>📄</span> Documents Required Checklist
            </h3>
            <div className="grid-3" style={{ gap: '20px' }}>
              <div className="docs-group">
                <h4 style={{ color: 'var(--color-primary)' }}>1. KYC Documents</h4>
                <ul className="docs-list" style={{ listStyle: 'square', marginLeft: '18px', fontSize: '0.9rem', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                  <li>Aadhaar Card / Voter ID</li>
                  <li>PAN Card (Mandatory)</li>
                  <li>Passport-sized Photographs</li>
                </ul>
              </div>
              <div className="docs-group">
                <h4 style={{ color: 'var(--color-primary)' }}>2. Financial Proofs</h4>
                <ul className="docs-list" style={{ listStyle: 'square', marginLeft: '18px', fontSize: '0.9rem', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                  <li>Last 3 Months Salary Slips</li>
                  <li>Form 16 / Income Tax Returns</li>
                  <li>Last 6 Months Bank Statement</li>
                </ul>
              </div>
              <div className="docs-group">
                <h4 style={{ color: 'var(--color-primary)' }}>3. Property Papers</h4>
                <ul className="docs-list" style={{ listStyle: 'square', marginLeft: '18px', fontSize: '0.9rem', color: 'var(--color-text-muted)', marginTop: '8px' }}>
                  <li>Sale Agreement / Allotment Letter</li>
                  <li>No Objection Certificate (NOC)</li>
                  <li>Approved Layout Drawings</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
